import { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { CTA, trackAttrs } from '../utils/track'
import { MAVEN_PROFILE_URL, type Offer } from '../config/cta'
import MavenOffers from '../components/sections/MavenOffers/MavenOffers'
import { FaArrowRight, FaGraduationCap } from 'react-icons/fa'
import CourseList from '../components/sections/CourseList/CourseList'
import InlineHighlight from '../components/content/InlineHighlight/InlineHighlight'
import { ContentColumn } from '../components/layout/ContentColumn/ContentColumn'

function CoursesPage({ items = [], maven = [] }: { items?: any[]; maven?: Offer[] }) {
  const posts = useMemo(() => {
    return {
      data: items.map(item => ({
        ...item,
        createdAt: item.createdAt || item.created_at,
        updatedAt: item.updatedAt || item.updated_at,
        publishedAt: item.publishedAt || item.published_at,
      })),
    }
  }, [])

  useEffect(() => {
    const measure = () => {
      const headerEl = document.querySelector('header') as HTMLElement | null
      const stickyNavEl = document.querySelector('.sticky.top-0') as HTMLElement | null
      const headerH = headerEl?.offsetHeight ?? 0
      const navH = stickyNavEl?.offsetHeight ?? 0
      const offset = headerH + navH
      document.documentElement.style.setProperty('--header-nav-height', `${offset}px`)
    }
    measure()
    requestAnimationFrame(() => measure())
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  const totalCourses = posts.data.length
  const cover = (c: any) => c.image?.formats?.medium?.url || c.image?.url || (typeof c.image === "string" ? c.image : undefined)
  const covers = posts.data.filter((c: any) => cover(c)).slice(0, 3)

  return (
    <>

      <div className="min-h-screen bg-brand-cream">
        <section className="relative overflow-hidden bg-brand-navy py-20 md:py-28">
          <ContentColumn anchor="courses-hero" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="flex flex-col">
              <span className="inline-flex items-center gap-2 w-fit mb-8 font-body text-body-sm text-white/80">
                <FaGraduationCap className="text-base" aria-hidden="true" />
                {maven.length ? `Live on Maven · ${totalCourses} self-paced` : `Self-paced courses · ${totalCourses} available`}
              </span>

              <h1 className="font-heading text-display font-bold text-white mb-6">
                Learn live, or at your own pace, <InlineHighlight color="sky">on demand</InlineHighlight>
              </h1>

              <p className="font-body text-body-lg text-white/85 mb-8 max-w-2xl">
                Live cohorts on Maven when you want a group and a deadline, or self-paced courses you buy once and keep. All designed by a Principal Engineer with 16+ years of shipping.
              </p>

              {/* Disambiguate the two models: these are self-paced; the flagship
                  live, cohort-based experience is on Maven. */}
              <a
                href={maven.length ? '#live-on-maven' : MAVEN_PROFILE_URL}
                {...(maven.length ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
                {...trackAttrs(CTA.maven, 'courses_page')}
                className="inline-flex items-center gap-2 w-fit mb-10 rounded-smooth border border-white/25 bg-white/[0.06] px-4 py-2.5 font-heading text-body-sm font-semibold text-white transition-colors hover:bg-white/[0.12]"
              >
                {maven.length ? 'Prefer live teaching? See the live cohorts and free sessions on Maven ↓' : 'Prefer a live, cohort-based experience? See live cohorts on Maven ↗'}
              </a>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#courses-list"
                  className="group inline-flex items-center justify-center gap-2 bg-white text-brand-navy font-heading text-cta font-bold px-8 py-4 rounded-smooth hover:opacity-90 transition-opacity duration-150"
                >
                  Browse Courses
                  <FaArrowRight className="text-sm transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden="true" />
                </a>
                <Link
                  to="/mentorship"
                  {...trackAttrs(CTA.mentorship, 'courses_page')}
                  className="inline-flex items-center justify-center gap-2 bg-transparent text-white border-2 border-white/40 font-heading text-cta font-bold px-8 py-4 rounded-smooth hover:opacity-90 transition-opacity duration-150"
                >
                  Get Mentored
                </Link>
              </div>
            </div>
            {covers.length ? (
              <div className="hidden lg:grid grid-cols-2 gap-4">
                {covers.map((c: any, i: number) => (
                  <img
                    key={c.slug || i}
                    src={cover(c)}
                    alt={c.title}
                    className={`w-full object-cover rounded-xl ring-1 ring-white/15 shadow-2xl ${i === 0 ? "col-span-2 aspect-[16/9]" : "aspect-[4/3]"}`}
                    loading="eager"
                  />
                ))}
              </div>
            ) : null}
            </div>
          </ContentColumn>
        </section>

        <div className="border-b border-border-subtle">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <nav aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 font-heading text-overline uppercase tracking-wider text-ink-muted">
                <li><Link to="/" className="hover:text-brand-navy transition-colors duration-150">Home</Link></li>
                <li aria-hidden="true">/</li>
                <li className="text-brand-navy" aria-current="page">Courses</li>
              </ol>
            </nav>
          </div>
        </div>

        {/* Live on Maven, above the self-paced list: it is the paid,
            time-limited offer, so it is the one a reader can miss. */}
        {maven.length ? (
          <section id="live-on-maven" className="py-16 md:py-20 scroll-mt-20 border-b border-border-subtle">
            <ContentColumn anchor="live-on-maven" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-2xl mb-10">
                <h2 className="font-heading text-h2 font-bold text-brand-navy mb-3">
                  Live on <InlineHighlight color="sky">Maven</InlineHighlight>
                </h2>
                <p className="font-body text-body text-ink-soft">
                  Cohorts taught live on Zoom, with sessions, projects and feedback, plus free live sessions you can join before you commit.
                </p>
              </div>
              <MavenOffers offers={maven} placement="courses_live" />
            </ContentColumn>
          </section>
        ) : null}

        <section id="courses-list" className="py-16 md:py-20 scroll-mt-20">
          <ContentColumn anchor="courses-list" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center mb-12">
              <h2 className="font-heading text-h2 font-bold text-brand-navy mb-3">
                Self-paced <InlineHighlight color="sky">courses</InlineHighlight>
              </h2>
              <p className="font-body text-body text-ink-soft">
                Buy once, learn on your own schedule. For live, cohort-based teaching, see the{' '}
                <a href={MAVEN_PROFILE_URL} target="_blank" rel="noopener noreferrer" {...trackAttrs(CTA.maven, 'courses_list')} className="font-semibold text-brand-navy underline decoration-brand-navy/40 hover:decoration-brand-navy/80">
                  live cohorts on Maven
                </a>.
              </p>
            </div>

            <CourseList name="Courses" pathName="/courses" data={posts} />
          </ContentColumn>
        </section>
      </div>
    </>
  )
}

export default CoursesPage
